/* eslint-disable max-len */
import React, { useRef, useState, useMemo } from "react";
import { Box, Button, styled, Typography } from "@mui/material";
import { generatePath, useNavigate } from "react-router";
import { AppRoutes, landDetailLink } from "../../config/app.routes";
import { toReadable } from "../../utils/methods";
import Waterfall from "../../assets/waterfall-blog.jpg";
import { AddressLink } from "../AddressLink";
import { formatSimilarityScore } from "../ApplicantReview/ApplicantDetails";
import { formatValue } from "../../utils/formatValue";
import { sixDigits, numberWithCommas } from "../../utils/methods";
import { COUNTRY_CODES } from "../../utils/constants";

// Types
interface OrgData {
  name?: string;
  phone?: string;
  country?: string;
  email?: string;
  address?: string;
  website?: string;
  secondaryWallets?: Trustee[];
  proofOfAddress?: string;
  nativeKycStatus?: string;
}

interface Trustee {
  name?: string;
  role: string;
  status: string;
  email?: string;
  address?: string;
  profilePicture?: string;
  patronymic?: string;
  country?: string;
  phone?: string;
  region?: string;
  proofOfId?: string;
  portfolio?: {
    totalVerifiedLandsCount?: number;
    portfolioSum?: number;
  };
  nativeKYCStatus?: {
    faceMatch?: {
      similarity_score?: number | string;
    };
  };
}

interface OrgLand {
  name?: string;
  id?: string;
  image?: string;
  content?: string;
}

interface OrgPopUpProps {
  open: boolean;
  onClose: () => void;
  orgData: OrgData | null;
  orgLands: OrgLand[] | null;
  singleTrustee?: boolean;
  singleTrusteeData?: Trustee | null;
  initialTab?: TabKey;
}

type TabKey = "organization" | "trustees" | "singleTrustee";

const headerHeight = 64;

// Styled components (similar to LandCardPopup)
const Overlay = styled(Box)({
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(0,0,0,0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1300,
  overflowY: "auto",
  padding: "16px",
  "@media (max-height: 620px)": {
    alignItems: "flex-start",
  },
});

const CardBox = styled(Box)({
  position: "relative",
  width: 320,
  maxWidth: "100%",
  maxHeight: 590,
  borderRadius: 20,
  backgroundColor: "transparent",
  overflow: "hidden",
  boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
  cursor: "default",
  display: "flex",
  flexDirection: "column",
});

const ScaleOuter = styled(Box)({ position: "relative" });
const ScaleInner = styled(Box)({
  position: "relative",
  width: 320,
  maxWidth: "100%",
  transformOrigin: "top left",
  display: "flex",
  flexDirection: "column",
});

const HeaderRow = styled(Box)({
  position: "relative",
  width: "100%",
  height: headerHeight,
  flexShrink: 0,
  display: "flex",
  flexDirection: "row",
});

const HalfArea = styled(Box)<{ side: "left" | "right"; active?: boolean }>(({ side, active }) => ({
  width: "50%",
  height: headerHeight,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  background: active ? "#ffffff" : "transparent",
  borderTopLeftRadius: side === "left" && active ? 20 : 20,
  borderTopRightRadius: side === "right" && active ? 20 : 20,
}));

const PillBox = styled(Box)({
  width: "100%",
  height: "100%",
  borderRadius: 20,
  background: "#ede1dc",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
});

const TabLabelButton = styled(Button)(({ theme }) => ({
  position: "relative",
  height: headerHeight,
  width: "100%",
  minWidth: 0,
  padding: 0,
  color: "#000",
  fontFamily: "Inter, sans-serif",
  fontSize: 14,
  background: "transparent",
  border: "none",
  boxShadow: "none",
  transition: "none !important",
  display: "flex",
  justifyContent: "center",
  textAlign: "center",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  userSelect: "none",
  [theme.breakpoints.down("sm")]: {
    paddingTop: 0,
    alignItems: "center",
  },
  [theme.breakpoints.up("sm")]: {
    paddingTop: 0,
    alignItems: "center",
  },
  zIndex: 4,
  "&:hover": { background: "transparent", border: "none", boxShadow: "none", textDecoration: "none" },
  "&:active": { background: "transparent", boxShadow: "none" },
  "&:focus": { boxShadow: "none" },
}));

const Label = styled(Typography)({
  fontFamily: "Inter, sans-serif",
  fontSize: 14,
  lineHeight: "20px",
  color: "#000",
});

const Mono = styled(Typography)({
  fontFamily: "IBM Plex Mono, monospace",
  fontSize: 14,
  color: "#000",
});

const ContentContainer = styled(Box)({
  position: "relative",
  flex: 1,
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
});

// const ABox = styled(Box)<{
//   left?: number;
//   top?: number;
//   right?: number;
//   bottom?: number;
//   width?: number | string;
//   height?: number | string;
// }>(({ left, top, right, bottom, width, height }) => ({
//   position: "absolute",
//   ...(left !== undefined ? { left } : {}),
//   ...(top !== undefined ? { top } : {}),
//   ...(right !== undefined ? { right } : {}),
//   ...(bottom !== undefined ? { bottom } : {}),
//   ...(width !== undefined ? { width } : {}),
//   ...(height !== undefined ? { height } : {}),
// }));

// Mini hero card styled components (matching EcoBank Hero style)
const HeroCard = styled(Box)({
  position: "relative",
  width: "100%",
  height: 298,
  borderRadius: "20px",
  overflow: "hidden",
  flexShrink: 0,
  cursor: "pointer",
});

const HeroCardImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  position: "absolute",
  top: 0,
  left: 0,
  borderRadius: "20px",
});

const HeroCardBlur = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  backgroundColor: "rgba(43, 53, 1, 0.15)",
  backdropFilter: "blur(30px)",
  maskImage: "linear-gradient(to bottom, transparent 0%, black 85%)",
  WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, black 85%)",
  pointerEvents: "none",
  zIndex: 1,
  borderRadius: "20px",
});

const HeroCardContent = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  padding: "26px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  zIndex: 2,
  boxSizing: "border-box",
});

const ContentBackground = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  backgroundColor: "#fff",
  borderBottomLeftRadius: 20,
  borderTopLeftRadius: 20,
  borderTopRightRadius: 0,
  borderBottomRightRadius: 20,
  minHeight: 0,
});

const ScrollArea = styled(Box)(() => ({
  flex: 1,
  overflowY: "auto",
  minHeight: 0,
  "&::-webkit-scrollbar": { width: 0, height: 0 },
  "&::-webkit-scrollbar-thumb": { background: "transparent" },
  scrollbarWidth: "none",
  msOverflowStyle: "none",
}));

const TrusteeProfilePicture = styled("img")({
  fontSize: "170px",
  borderRadius: "50%",
  height: 47,
  width: 47,
  border: "none",
  cursor: "pointer",
});

const WhiteSection = styled(Box)<{
  height?: number | string;
  roundedTopRight?: boolean;
  roundedTopLeft?: boolean;
  roundedAll?: boolean;
}>(({ height, roundedTopRight, roundedAll, roundedTopLeft }) => ({
  width: "100%",
  ...(height ? { height } : {}),
  backgroundColor: "#ffffff",
  borderTopRightRadius: roundedTopRight ? 20 : 0,
  borderTopLeftRadius: roundedTopLeft || roundedAll ? 20 : 0,
  borderBottomLeftRadius: roundedAll ? 20 : 20,
  borderBottomRightRadius: roundedAll ? 20 : 20,
  flexShrink: 0,
}));

// Organization View Component
const OrganizationView: React.FC<{
  orgData: OrgData | null;
  orgLands: any[] | null; // Using any[] to match LandToken properties without deep type imports
  singleTrustee?: boolean;
  singleTrusteeData?: any;
}> = ({ orgData, orgLands, singleTrustee, singleTrusteeData }) => {
  const navigate = useNavigate();

  // EcoBank Hero status logic

  let land = orgLands && orgLands.length > 0 ? orgLands[0] : null;

  if (singleTrusteeData && singleTrusteeData.recentLandName && orgLands) {
    const foundLand = orgLands.find(
      (l: any) => l.name === singleTrusteeData.recentLandName || l?.meta?.name === singleTrusteeData.recentLandName
    );
    if (foundLand) {
      land = foundLand;
    }
  }

  console.log("Resolved land in OrganizationView:", land);
  console.log("Offer Status:", land?.offerStatus);

  const location = React.useMemo(() => {
    if (land?.meta?.location) {
      try {
        return JSON.parse(land.meta.location as any);
      } catch (e) {
        console.error("Failed to parse location", e);
        return null;
      }
    }
    return null;
  }, [land]);

  return (
    <ContentContainer sx={{ pb: 0 }}>
      {/* Top white section */}
      <WhiteSection height={"fit-content"} roundedTopRight>
        {/* Contact Information */}
        <Box sx={{ p: 12 }}>
          <Box sx={{ display: "flex", mb: "8px" }}>
            <Label sx={{ fontWeight: 700 }}>{orgData?.name || "N/A"}</Label>
          </Box>
          <Box sx={{ display: "flex", mb: "2px" }}>
            <Label sx={{ color: "#000" }}>Address:</Label>
            <Label sx={{ ml: 2 }}>{orgData?.address || "N/A"}</Label>
          </Box>
          <Box sx={{ display: "flex", mb: "2px" }}>
            <Label sx={{ color: "#000" }}>Country:</Label>
            <Label sx={{ ml: 2 }}>{orgData?.country || "N/A"}</Label>
          </Box>
          <Box sx={{ display: "flex", mb: "2px" }}>
            <Label sx={{ color: "#000" }}>Phone:</Label>
            <Label sx={{ ml: 2 }}>{orgData?.phone || "N/A"}</Label>
          </Box>
          <Box sx={{ display: "flex", mb: "2px" }}>
            <Label sx={{ color: "#000" }}>Email:</Label>
            <Label sx={{ ml: 2 }}>
              <a href={`mailto:${orgData?.email}`} style={{ all: "unset", cursor: "pointer", wordBreak: "break-word" }}>
                {orgData?.email || "N/A"}
              </a>
            </Label>
          </Box>
          <Box sx={{ display: "flex", mb: "2px" }}>
            <Label sx={{ color: "#000" }}>Web:</Label>
            <Label sx={{ ml: 2 }}>
              <a
                href={orgData?.website?.startsWith("http") ? orgData?.website : `https://${orgData?.website}`}
                style={{ all: "unset", cursor: "pointer" }}
                target="_blank"
                rel="noopener noreferrer"
              >
                {orgData?.website || "N/A"}
              </a>
            </Label>
          </Box>
          {singleTrustee && (
            <Box sx={{ display: "flex", mb: "2px" }}>
              <Label sx={{ color: "#000", minWidth: "80px" }}>Proof of Address:</Label>
              <Label sx={{ ml: 2 }}>
                {orgData?.proofOfAddress ? (
                  <a
                    href={orgData.proofOfAddress}
                    style={{ all: "unset", cursor: "pointer", textDecoration: "underline" }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View
                  </a>
                ) : (
                  "N/A"
                )}
              </Label>
            </Box>
          )}
        </Box>
      </WhiteSection>

      {/* Hero card — EcoBank style */}
      <HeroCard
        onClick={() => {
          const firstId = orgLands && orgLands.length > 0 ? orgLands[0]?.id : undefined;
          if (firstId) {
            navigate(landDetailLink(firstId));
          }
        }}
        sx={{ cursor: orgLands && orgLands.length > 0 ? "pointer" : "default" }}
      >
        <HeroCardImage
          src={land?.content ? land?.content : Waterfall}
          alt={orgLands && orgLands.length > 0 ? orgLands[0]?.name || "Land" : "Land"}
        />
        <HeroCardBlur />
        <HeroCardContent>
          {/* Mirrors Hero.tsx HeroItem layout exactly */}
          {/* Top section: "Recently Verified" label + land name */}
          <Box sx={{ mb: "20px" }}>
            {/* "Recently Verified" — extra label above name */}
            <Typography
              sx={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 700,
                fontSize: "16px",
                lineHeight: 1.21,
                color: "#fff",
                mb: "4px",
              }}
            >
              Recently Verified
            </Typography>
          </Box>

          {/* Bottom stats row — equivalent to StatsGrid */}
          {land && (
            <div>
              {/* Land name — equivalent to NameText */}

              {/* Location */}
              <Typography
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 700,
                  fontSize: "26px",
                  lineHeight: 1.21,
                  color: "#fff",
                }}
              >
                {land?.meta?.name || land?.name || ""}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 700,
                  fontSize: "16px",
                  lineHeight: 1.2,
                  color: "#fff",
                  opacity: 0.8,
                  mt: "4px",
                  mb: "24px",
                }}
              >
                {location?.name}, {location?.country}
              </Typography>

              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px", alignItems: "end" }}>
                {/* SCORE */}
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "start" }}>
                  <Typography
                    sx={{
                      fontFamily: "IBM Plex Mono, monospace",
                      fontWeight: 700,
                      fontSize: "16px",
                      lineHeight: 1.3,
                      color: "rgba(255,255,255,0.5)",
                      textTransform: "uppercase",
                      mb: "4px",
                    }}
                  >
                    SCORE
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "IBM Plex Mono, monospace",
                      fontWeight: 700,
                      fontSize: "16px",
                      lineHeight: 1.3,
                      color: "#fff",
                      textTransform: "uppercase",
                    }}
                  >
                    {land.ecoScore ? (land.ecoScore * 100).toFixed(0) : "0"}
                  </Typography>
                </Box>

                {/* TR3 */}
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "start" }}>
                  <Typography
                    sx={{
                      fontFamily: "IBM Plex Mono, monospace",
                      fontWeight: 700,
                      fontSize: "16px",
                      lineHeight: 1.3,
                      color: "rgba(255,255,255,0.5)",
                      textTransform: "uppercase",
                      mb: "4px",
                    }}
                  >
                    TR3
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "IBM Plex Mono, monospace",
                      fontWeight: 700,
                      fontSize: "16px",
                      lineHeight: 1.3,
                      color: "#fff",
                      textTransform: "uppercase",
                    }}
                  >
                    {(() => {
                      // Mirrors LandCardPopup: show remaining trees when the easement
                      // is activated, otherwise fall back to the total for the easement.
                      // Pre-mint tokens have no etNft, so every read here must be guarded —
                      // formatValue(undefined) throws on .toLocaleString.
                      const totalTrees =
                        land.etNft?.totalTreeForEasement != null
                          ? formatValue(land.etNft.totalTreeForEasement as any, false)
                          : null;
                      const formattedTrees =
                        totalTrees != null ? sixDigits(formatValue(totalTrees as any, true, 0)) : null;
                      const remainingTrees = land.etNft?.easementActivated
                        ? Number(formatValue(land.etNft.totalTreeForEasement as any, false)) -
                          Number(formatValue(land.etNft.easementActivated.rewardClaimed as any, false) || 0)
                        : null;
                      const formattedRemainingTrees =
                        remainingTrees !== null ? String(numberWithCommas(sixDigits(remainingTrees))) : null;
                      return (
                        <Box sx={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
                          {formattedRemainingTrees ?? formattedTrees ?? "N/A"}
                        </Box>
                      );
                    })()}
                  </Typography>
                </Box>

                {/* No — token number */}
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "end", width: "100%" }}>
                  <Typography
                    sx={{
                      fontFamily: "IBM Plex Mono, monospace",
                      fontWeight: 700,
                      fontSize: "16px",
                      lineHeight: 1.3,
                      color: "rgba(255,255,255,0.5)",
                      textTransform: "uppercase",
                      mb: "4px",
                    }}
                  >
                    No
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "IBM Plex Mono, monospace",
                      fontWeight: 700,
                      fontSize: "16px",
                      lineHeight: 1.3,
                      color: "#fff",
                      textTransform: "uppercase",
                    }}
                  >
                    {land.etNft?.id != null ? String(land.etNft.id) : "N/A"}
                  </Typography>
                </Box>
              </Box>
            </div>
          )}
        </HeroCardContent>
      </HeroCard>
    </ContentContainer>
  );
};

// Single Trustee View Component
const SingleTrusteeView: React.FC<{
  trusteeData: Trustee | null;
  orgLands: OrgLand[] | null;
}> = ({ trusteeData, orgLands }) => {
  console.log("Trustee Data", trusteeData);
  console.log("Org Lands", orgLands);
  const navigate = useNavigate();
  return (
    <ContentContainer sx={{ pb: 0 }}>
      {/* Top white section */}
      <WhiteSection height={"fit-content"} roundedTopLeft>
        {/* Contact Information */}
        <Box sx={{ p: 12 }}>
          <Box sx={{ display: "flex", mb: "2px" }}>
            <Label sx={{ fontWeight: 700 }}>{trusteeData?.name || "N/A"}</Label>
          </Box>
          <Box sx={{ display: "flex", mb: "2px" }}>
            <Label sx={{ color: "#666", minWidth: "80px" }}>Phone:</Label>
            <Label sx={{ ml: 2 }}>{trusteeData?.phone || "N/A"}</Label>
          </Box>
          <Box sx={{ display: "flex", mb: "2px" }}>
            <Label sx={{ color: "#666", minWidth: "80px" }}>Country:</Label>
            <Label sx={{ ml: 2 }}>{trusteeData?.country || "N/A"}</Label>
          </Box>
          <Box sx={{ display: "flex", mb: "2px" }}>
            <Label sx={{ color: "#666", minWidth: "80px" }}>Region:</Label>
            <Label sx={{ ml: 2 }}>{trusteeData?.patronymic || "N/A"}</Label>
          </Box>
          <Box sx={{ display: "flex", mb: "2px" }}>
            <Label sx={{ color: "#666", minWidth: "80px" }}>Email:</Label>
            <Label sx={{ ml: 2 }}>
              <a
                href={`mailto:${trusteeData?.email}`}
                style={{ all: "unset", cursor: "pointer", wordBreak: "break-word" }}
              >
                {trusteeData?.email || "N/A"}
              </a>
            </Label>
          </Box>
          <Box sx={{ display: "flex", mb: "2px" }}>
            <Label sx={{ color: "#666", minWidth: "80px" }}>Address:</Label>
            <Label sx={{ ml: 1, fontSize: 12 }}>
              <AddressLink address={trusteeData?.address || ""} small type={"trust"} sx={{ all: "unset" }} />
            </Label>
          </Box>
          {/* <Box sx={{ display: "flex", mb: "2px" }}>
              <Label sx={{ color: "#666", minWidth: "80px" }}>Proof of ID:</Label>
              <Label sx={{ ml: 2 }}>
                {trusteeData?.proofOfId ? (
                  <a
                    href={trusteeData.proofOfId}
                    style={{ all: "unset", cursor: "pointer", textDecoration: "underline" }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View
                  </a>
                ) : (
                  "N/A"
                )}
              </Label>
            </Box> */}
          <Box sx={{ display: "flex", mb: "2px" }}>
            <Label sx={{ color: "#666", minWidth: "80px" }}>Face Similarity</Label>
            <Label sx={{ ml: 2 }}>
              {formatSimilarityScore(trusteeData?.nativeKYCStatus?.faceMatch?.similarity_score)}
            </Label>
          </Box>
        </Box>
      </WhiteSection>

      {/* Hero card — EcoBank style */}
      <HeroCard
        onClick={() => {
          if (trusteeData?.address) {
            navigate(generatePath(AppRoutes.UserWallet.path, { address: trusteeData.address }));
          }
        }}
        sx={{ cursor: trusteeData?.address ? "pointer" : "default", flex: 1, height: "auto" }}
      >
        <HeroCardImage
          src={
            trusteeData?.profilePicture ||
            (orgLands && orgLands.length > 0 ? orgLands[0]?.image || Waterfall : Waterfall)
          }
          alt={trusteeData?.name || "Trustee"}
        />
        <HeroCardBlur />
        <HeroCardContent>
          {trusteeData?.name && (
            <Typography
              sx={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 700,
                fontSize: "26px",
                lineHeight: 1.21,
                color: "#fff",
                mb: "4px",
              }}
            >
              {trusteeData.name}
            </Typography>
          )}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography
              sx={{
                fontFamily: "IBM Plex Mono, monospace",
                fontWeight: 700,
                fontSize: "12px",
                color: "rgba(255,255,255,0.5)",
                lineHeight: 1.3,
                textTransform: "uppercase",
              }}
            >
              {trusteeData?.role === "user" ? "View User" : "View Trustee"}
            </Typography>
            <Typography
              sx={{
                fontFamily: "IBM Plex Mono, monospace",
                fontWeight: 700,
                fontSize: "12px",
                color: "#fff",
                lineHeight: 1.3,
                textTransform: "uppercase",
              }}
            >
              Profile
            </Typography>
          </Box>
        </HeroCardContent>
      </HeroCard>
    </ContentContainer>
  );
};

// Trustees View Component
const TrusteesView: React.FC<{
  orgData: OrgData | null;
  onClose: () => void;
}> = ({ orgData, onClose }) => {
  const navigate = useNavigate();

  const activeTrustees = useMemo(() => {
    const filtered =
      orgData?.secondaryWallets?.filter((trustee) => trustee?.role === "trust" && trustee?.status === "active") || [];
    // Duplicate trustees x3 for testing
    return [...filtered];
  }, [orgData]);

  const handleClose = () => {
    onClose();
  };

  return (
    <ContentContainer>
      <ContentBackground>
        {/* Column Headers */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "55px 1fr 1fr 1fr",
            columnGap: "24px",
            alignItems: "start",
            px: "25px",
            py: "18px",
            borderBottom: "1px solid #E0E0E0",
            flexShrink: 0,
          }}
        >
          <Mono sx={{ fontSize: 12, textTransform: "uppercase" }}>Trustee</Mono>
          <Mono sx={{ fontSize: 12, textTransform: "uppercase" }}>Region</Mono>
          <Mono sx={{ fontSize: 12, textTransform: "uppercase", textAlign: "center" }}>Lands</Mono>
          <Mono sx={{ fontSize: 12, textTransform: "uppercase", textAlign: "right" }}>Portf</Mono>
        </Box>

        {/* Scrollable content region */}
        <ScrollArea sx={{ px: 0 }}>
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            {activeTrustees.map((trustee, index) => (
              <Box
                key={`${trustee?.email}-${index}`}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "55px 1fr 1fr 1fr",
                  columnGap: "24px",
                  alignItems: "start",
                  px: "25px",
                  py: "18px",
                  borderBottom: "1px solid #E0E0E0",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                }}
                onClick={() => {
                  if (trustee?.address) {
                    navigate(generatePath(AppRoutes.UserWallet.path, { address: trustee.address }));
                    handleClose();
                  }
                }}
              >
                <TrusteeProfilePicture
                  src={trustee?.profilePicture}
                  alt={`${trustee?.patronymic || "Trustee"} profile`}
                  style={{ height: 42, width: 42 }}
                />
                <Box>
                  <Mono sx={{ fontSize: 12, textTransform: "uppercase" }}>{trustee?.patronymic || "N/A"}</Mono>
                  <Mono sx={{ fontSize: 12, color: "#666", textTransform: "uppercase" }}>
                    {COUNTRY_CODES.find((c) => c.name === trustee?.country)?.abv || trustee?.country || "N/A"}
                  </Mono>
                </Box>
                <Mono sx={{ fontSize: 12, textTransform: "uppercase", textAlign: "center" }}>
                  {trustee?.portfolio?.totalVerifiedLandsCount || 0}
                </Mono>
                <Box sx={{ textAlign: "right" }}>
                  <Mono sx={{ fontSize: 12 }}>{toReadable(trustee?.portfolio?.portfolioSum || 0)}</Mono>
                  <Mono sx={{ fontSize: 12, color: "#666" }}>USD</Mono>
                </Box>
              </Box>
            ))}
          </Box>
        </ScrollArea>
      </ContentBackground>
    </ContentContainer>
  );
};

// Main Component
const OrgPopUp: React.FC<OrgPopUpProps> = ({
  open,
  onClose,
  orgData,
  orgLands,
  singleTrustee = false,
  singleTrusteeData = null,
  initialTab,
}) => {
  const [tab, setTab] = useState<TabKey>(initialTab || "organization");
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const [scale] = useState(1);

  // Sync the tab to `initialTab` during render (not in an effect). The component
  // stays mounted between opens, so an effect-based sync would paint the stale
  // tab first and visibly flicker to the correct one. Adjusting state while
  // rendering re-renders immediately, before anything reaches the screen.
  const [prevInitialTab, setPrevInitialTab] = React.useState(initialTab);
  if (initialTab && initialTab !== prevInitialTab) {
    setPrevInitialTab(initialTab);
    setTab(initialTab);
  }

  const onOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === overlayRef.current) {
      onClose();
    }
  };

  const onOverlayKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape" || e.key === "Enter" || e.key === " ") {
      onClose();
    }
  };

  // const theme = useTheme();
  // const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (!open) {
    return null;
  }

  const tabHeader = (
    <HeaderRow
      sx={{
        backgroundImage: "linear-gradient(to bottom, transparent 50%, white 50%)",
        borderRadius: "20px",
      }}
    >
      <HalfArea side="left" active={tab === "organization"}>
        {tab !== "organization" ? (
          <PillBox onClick={() => setTab("organization")}>
            <TabLabelButton
              disableFocusRipple
              disableRipple
              disableTouchRipple
              onClick={() => setTab("organization")}
              sx={{
                width: "auto",
                height: "auto",
                minWidth: 0,
                padding: "0 12px",
                color: "#000",
                textDecoration: "underline",
                textUnderlineOffset: "3px",
              }}
            >
              Organization
            </TabLabelButton>
          </PillBox>
        ) : (
          <TabLabelButton disableFocusRipple disableRipple disableTouchRipple onClick={() => setTab("organization")}>
            Organization
          </TabLabelButton>
        )}
      </HalfArea>
      <HalfArea side="right" active={tab === (singleTrustee ? "singleTrustee" : "trustees")}>
        {singleTrustee ? (
          tab !== "singleTrustee" ? (
            <PillBox onClick={() => setTab("singleTrustee")}>
              <TabLabelButton
                disableFocusRipple
                disableRipple
                disableTouchRipple
                onClick={() => setTab("singleTrustee")}
                sx={{ width: "auto", height: "auto", minWidth: 0, padding: "0 12px", color: "#000" }}
              >
                Trustee
              </TabLabelButton>
            </PillBox>
          ) : (
            <TabLabelButton disableFocusRipple disableRipple disableTouchRipple onClick={() => setTab("singleTrustee")}>
              Trustee
            </TabLabelButton>
          )
        ) : tab !== "trustees" ? (
          <PillBox onClick={() => setTab("trustees")}>
            <TabLabelButton
              disableFocusRipple
              disableRipple
              disableTouchRipple
              onClick={() => setTab("trustees")}
              sx={{
                width: "auto",
                height: "auto",
                minWidth: 0,
                padding: "0 12px",
                color: "#000",
                textDecoration: "underline",
                textUnderlineOffset: "3px",
              }}
            >
              Activity
            </TabLabelButton>
          </PillBox>
        ) : (
          <TabLabelButton disableFocusRipple disableRipple disableTouchRipple onClick={() => setTab("trustees")}>
            Activity
          </TabLabelButton>
        )}
      </HalfArea>
    </HeaderRow>
  );

  const tabContent = (
    <Box sx={{ position: "relative", flex: 1, minHeight: 0 }}>
      <Box sx={{ visibility: "hidden", pointerEvents: "none" }}>
        <OrganizationView
          orgData={orgData}
          orgLands={orgLands}
          singleTrustee={singleTrustee}
          singleTrusteeData={singleTrusteeData}
        />
      </Box>
      <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column" }}>
        {tab === "organization" ? (
          <OrganizationView
            orgData={orgData}
            orgLands={orgLands}
            singleTrustee={singleTrustee}
            singleTrusteeData={singleTrusteeData}
          />
        ) : tab === "singleTrustee" ? (
          <SingleTrusteeView trusteeData={singleTrusteeData} orgLands={orgLands} />
        ) : (
          <TrusteesView orgData={orgData} onClose={onClose} />
        )}
      </Box>
    </Box>
  );

  // if (isMobile) {
  //   return (
  //     <SwipeableDrawer
  //       anchor="bottom"
  //       open={open}
  //       onClose={onClose}
  //       onOpen={() => { }}
  //       disableSwipeToOpen
  //       ModalProps={{ keepMounted: false }}
  //       PaperProps={{
  //         sx: {
  //           borderTopLeftRadius: 20,
  //           borderTopRightRadius: 20,
  //           overflow: "hidden",
  //           display: "flex",
  //           flexDirection: "column",
  //           maxHeight: "90vh",
  //         },
  //       }}
  //     >
  //       {/* Puller handle */}
  //       <Box sx={{ display: "flex", justifyContent: "center", pt: "10px", pb: "4px", flexShrink: 0 }}>
  //         <Box sx={{ width: 40, height: 4, borderRadius: 2, backgroundColor: "#ccc" }} />
  //       </Box>
  //       {tabHeader}
  //       {tabContent}
  //     </SwipeableDrawer>
  //   );
  // }

  return (
    <Overlay
      ref={overlayRef}
      onClick={onOverlayClick}
      onKeyDown={onOverlayKeyDown}
      role="button"
      aria-label="Close popup"
      tabIndex={0}
      sx={{
        backdropFilter: "blur(8px)",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
      }}
    >
      <ScaleOuter sx={{ width: 320, maxWidth: "100%" }}>
        <ScaleInner sx={{ transform: `scale(${scale})` }}>
          <CardBox role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            {tabHeader}
            {tabContent}
          </CardBox>
        </ScaleInner>
      </ScaleOuter>
    </Overlay>
  );
};

export default OrgPopUp;
export { DialogPopUp } from "./DialogPopUp";
